import { useMemo } from 'react';
import { useRaceTypes } from '@/hooks/useRaceTypes';
import { StatusBadge, ActionButtons, editAction, deleteAction } from '@/components/shared';
import {
  getBibNumberingModeDescription,
  getBibNumberingModeLabel,
  normalizeBibNumberingConfig,
} from '@/utils/bibNumbering';
import { RaceTypesManagerProps } from './racetypesmanager.props';
import { useRaceTypesActions } from './useRaceTypesActions';
import RaceTypesManagerView from './racetypesmanager.view';

const RaceTypesManager: React.FC<RaceTypesManagerProps> = ({ raceId, raceName, embedded }) => {
  const { data: raceTypes = [], isLoading: loading } = useRaceTypes(raceId);
  const actions = useRaceTypesActions(raceId);

  const raceTypeColumns = useMemo(() => [
    { key: 'name', label: 'Име', cellClassName: 'whitespace-nowrap font-medium text-gray-900' },
    { key: 'distance', label: 'Дистанца', cellClassName: 'whitespace-nowrap text-gray-600', render: (v: number) => v + ' км' },
    { key: 'price', label: 'Цена', cellClassName: 'whitespace-nowrap text-gray-600', render: (v: number) => v + ' МКД' },
    { key: 'packageType', label: 'Пакет', cellClassName: 'whitespace-nowrap', render: (v: string) => <StatusBadge label={v} variant="info" /> },
    {
      key: '_bibConfig',
      label: 'Нумерација',
      cellClassName: 'text-sm',
      render: (_: any, rt: any) => {
        const config = normalizeBibNumberingConfig(rt);
        const helper = getBibNumberingModeDescription(config);

        return (
          <div>
            <StatusBadge
              label={getBibNumberingModeLabel(config)}
              variant={config.bibNumberingEnabled ? 'info' : 'warning'}
            />
            <div className="mt-1 text-xs text-gray-500">{helper}</div>
          </div>
        );
      },
    },
    {
      key: 'startNumber',
      label: 'Опсег',
      cellClassName: 'whitespace-nowrap text-gray-600 text-sm',
      render: (v: number, rt: any) => v + '-' + rt.endNumber,
    },
    { key: 'maxParticipants', label: 'Учесници', cellClassName: 'whitespace-nowrap text-gray-600' },
    { key: 'numberOfZones', label: 'Зони', cellClassName: 'whitespace-nowrap text-gray-600' },
    {
      key: '_actions',
      label: 'Акции',
      className: 'text-right',
      cellClassName: 'whitespace-nowrap text-right',
      render: (_: any, rt: any) => (
        <ActionButtons actions={[editAction(() => actions.handleEdit(rt)), deleteAction(() => actions.handleDelete(rt.id))]} />
      ),
    },
  ], [actions]);

  return (
    <RaceTypesManagerView
      raceName={raceName}
      embedded={embedded}
      raceTypes={raceTypes}
      loading={loading}
      raceTypeColumns={raceTypeColumns}
      isDialogOpen={actions.isDialogOpen}
      setIsDialogOpen={actions.setIsDialogOpen}
      editingRaceType={actions.editingRaceType}
      formData={actions.formData}
      setFormData={actions.setFormData}
      handleCreate={actions.handleCreate}
      handleSubmit={actions.handleSubmit}
      deleteTarget={actions.deleteTarget}
      setDeleteTarget={actions.setDeleteTarget}
      confirmDelete={actions.confirmDelete}
    />
  );
};

export default RaceTypesManager;
